import type { FC, ReactNode } from 'react'

import { pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { EnquiryButton } from '@/components/generals/enquiry-button'
import { programmes } from '@/constants/pages'

interface AcademyProgrammesProps {
    index: string
}

/** Coaching programmes, each one pre-filling the lesson request. */
export const AcademyProgrammes: FC<AcademyProgrammesProps> = ({ index }): ReactNode => {
    return (
        <section id='programmes' aria-labelledby='programmes-heading' className='relative z-10 bg-pine text-bone'>
            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-10 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index={index} className='text-bone/70'>
                            Coaching programmes
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id='programmes-heading'
                            className='mt-6 text-[clamp(2.1rem,4.2vw,4.2rem)] font-light leading-[1.02] tracking-[-0.03em]'
                        >
                            From a first swing <em className='font-serif font-normal'>to a scratch handicap.</em>
                        </SplitReveal>
                    </div>

                    <p data-reveal className='self-end text-base leading-relaxed text-bone/70 lg:col-span-4 lg:col-start-9'>
                        Every programme is led by one of our eleven PGA professionals and open to members and visitors alike.
                    </p>
                </div>

                <ul className='mt-14 grid gap-5 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3 lg:gap-6'>
                    {programmes.map((programme, programmeIndex) => (
                        <li
                            key={programme.name}
                            data-reveal
                            className='group flex flex-col rounded-[6px] border border-bone/15 bg-bone/[0.03] p-6 transition-[translate,background-color,border-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1.5 hover:border-bone/35 hover:bg-bone/[0.06] lg:p-8'
                        >
                            <div className='flex items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/55'>
                                <span className='tabular-nums'>
                                    {pad(programmeIndex + 1)}
                                </span>

                                <span className='rounded-full border border-bone/20 px-3 py-1 text-ochre'>
                                    {programme.length}
                                </span>
                            </div>

                            <h3 className='type-wide mt-10 text-2xl font-medium uppercase leading-tight'>
                                {programme.name}
                            </h3>

                            <p className='mt-3 text-[15px] leading-relaxed text-bone/70'>
                                {programme.description}
                            </p>

                            <ul className='mt-6 flex flex-wrap gap-2'>
                                {programme.includes.map((item) => (
                                    <li key={item} className='rounded-full border border-bone/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-bone/75'>
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-auto pt-10'>
                                <EnquiryButton
                                    prefill={{ type: 'lesson', lessonFocus: programme.focus }}
                                    label='Request'
                                    ariaLabel={`Request ${programme.name}`}
                                    variant='outline-light'
                                    className='w-full justify-between'
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
